import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { configureStatusBar } from "../lib/statusBar";
import { Milk, Phone, KeyRound, ChevronLeft, MessageCircle, Send, Globe, ChevronDown } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useT, LANGUAGES } from "../lib/i18n";
import { getTenantSlug, setTenantSlug } from "../lib/tenant";
import { splitPhone, joinPhone, COUNTRY_CODES } from "../lib/phone";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import LegalFooter from "../components/LegalFooter";

export default function CustomerLogin() {
  const nav = useNavigate();
  const [search] = useSearchParams();
  const returnTo = search.get("return") || "/app/home";
  const { login } = useAuth();
  const { t } = useT();
  const { lang, setLang } = useT();
  // Resolve tenant once on mount — driven by subdomain / ?t= / localStorage.
  const tenantSlug = getTenantSlug();
  const [step, setStep] = useState("phone");
  const [cc, setCc] = useState("+91");
  const [local, setLocal] = useState("");
  const phone = joinPhone(cc, local);
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [channel, setChannel] = useState("sms");      // default SMS
  const [tenant, setTenant] = useState(null);
  // Single authoritative boolean computed by the backend
  // (routes_referrals.signup_referral_available): the admin setting AND the
  // referral addon actually being active for this tenant. Deriving it here
  // from two separate fields would let the field appear on a tenant whose
  // server then discards the code.
  //
  // `!== false` rather than `=== true` so a failed/slow tenant fetch degrades
  // to today's behaviour instead of hiding the field on a tenant that does
  // support referrals; the backend is the one that decides either way.
  const referralAtSignup = tenant?.referral_signup_enabled !== false;
  const [loading, setLoading] = useState(false);
  const [userExists, setUserExists] = useState(false);  // hide name field for returning users
  const [resendSecs, setResendSecs] = useState(0);
  // Referral code: prefilled from ?ref= query param (deep link from Share),
  // editable until OTP is verified. Only sent at verify time for NEW customers.
  const [refCode, setRefCode] = useState((search.get("ref") || "").toUpperCase());

  // Resolve tenant once on mount — driven by subdomain / ?t= / localStorage.
  // (Already declared above so the demo prefill conditional works.)

  useEffect(() => {
    setTenantSlug(tenantSlug);
    api.get(`/api/public/tenant/${tenantSlug}`)
      .then(({ data }) => setTenant(data.tenant))
      .catch(() => setTenant({ name: "Dairy Store", slug: tenantSlug, branding: {} }));
  }, [tenantSlug]);
  useEffect(() => { configureStatusBar({ bgColor: "#FAF9F6", style: "dark" }); }, []);

  // 30-second resend countdown — clears on phone-step return so a new
  // number / channel choice starts fresh.
  useEffect(() => {
    if (resendSecs <= 0) return undefined;
    const id = setInterval(() => setResendSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [resendSecs]);

  const sendOtp = async (overrideChannel) => {
    if (local.replace(/\D/g, "").length < 10) {
      toast.error("Enter a 10-digit phone number"); return;
    }
    const useChannel = overrideChannel || channel;
    setLoading(true);
    try {
      // Look up whether this customer already exists — lets us skip the
      // name field on returning logins (we only ask for the name once).
      try {
        const { data } = await api.get("/api/auth/user/exists", {
          params: { phone, role: "customer", tenant_slug: tenantSlug },
        });
        setUserExists(!!data.exists);
        if (data.exists && data.name) setName(data.name);
      } catch { /* not fatal */ }

      const { data } = await api.post("/api/auth/otp/request",
        { phone, tenant_slug: tenantSlug, role: "customer", channel: useChannel });
      toast.success(data.message || `OTP sent via ${useChannel.toUpperCase()}`);
      setChannel(useChannel);
      setStep("otp");
      setResendSecs(data.resend_after || 30);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send OTP");
    } finally { setLoading(false); }
  };

  const resendOtp = (resendChannel) => {
    if (resendSecs > 0) return;
    setOtp("");
    sendOtp(resendChannel);
  };

  const verifyOtp = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/api/auth/otp/verify",
        { phone, otp, tenant_slug: tenantSlug, role: "customer", name,
          referral_code: !userExists && referralAtSignup ? (refCode || "").trim().toUpperCase() : "" });
      setTenantSlug(tenantSlug);
      login(data.token, data.user, data.tenant || null, data.refresh_token || null);
      toast.success(`Welcome to ${tenant?.name || "your dairy"}!`);
      nav(returnTo);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid OTP");
    } finally { setLoading(false); }
  };

  const primary = tenant?.branding?.primary_color || "#3A86FF";

  return (
    <div className="min-h-screen pt-safe bg-brand-cream flex flex-col" data-testid="customer-login-page">
      <div aria-hidden className="fixed top-0 left-0 right-0 z-40 bg-brand-cream pointer-events-none"
           style={{ height: "env(safe-area-inset-top, 0px)" }}/>
      <div className="p-4 sm:p-5 flex items-center justify-between gap-2">
        {/* Back to the store's home screen (guests can browse it). This used
            to be an "About" link to the platform landing page. */}
        <Link to="/app/home" aria-label="Back to home"
              className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center shrink-0"
              data-testid="back-btn">
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="relative inline-flex items-center" data-testid="lang-switcher-wrap">
            <Globe className="absolute left-3 w-4 h-4 text-neutral-500 pointer-events-none" />
            <select value={lang} onChange={(e) => setLang(e.target.value)}
                    className="appearance-none h-9 pl-9 pr-2 sm:pr-3 rounded-full bg-white border border-neutral-200 text-xs sm:text-sm font-bold text-neutral-700 cursor-pointer"
                    data-testid="login-lang-select"
                    aria-label="Choose language">
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>{l.native}</option>
              ))}
            </select>
          </div>
          <Link to="/startstore" className="text-xs sm:text-sm font-bold whitespace-nowrap" style={{ color: primary }} data-testid="start-store-link">
            {t("start_a_store")} →
          </Link>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 sm:px-5 pb-6 sm:pb-10">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-6 sm:mb-8">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center overflow-hidden shrink-0" style={{ background: primary }}>
              {tenant?.branding?.logo_url ? (
                <img src={tenant.branding.logo_url} alt="" className="w-full h-full object-cover"/>
              ) : (
                <Milk className="w-6 h-6 text-white" strokeWidth={2.5} />
              )}
            </div>
            <div className="min-w-0">
              <div className="font-heading font-extrabold text-xl sm:text-2xl tracking-tight truncate" data-testid="tenant-brand-name">
                {tenant?.name || "Dairy Store"}
              </div>
              <div className="text-[10px] sm:text-xs text-neutral-500 uppercase tracking-wider font-bold">Customer Login</div>
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-neutral-200 p-5 sm:p-7 shadow-sm">
            {step === "phone" ? (
              <>
                <h2 className="font-heading text-2xl font-bold mb-1">{t("sign_in_phone")}</h2>
                <p className="text-neutral-600 text-sm mb-6">{t("otp_subtitle")}</p>

                <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">{t("phone_number")}</label>
                <div className="flex gap-2 mt-2">
                  <div className="relative">
                    <select value={cc} onChange={(e) => setCc(e.target.value)}
                            className="appearance-none h-14 pl-3 pr-7 rounded-2xl border border-neutral-200 bg-white font-mono font-bold text-base cursor-pointer"
                            data-testid="customer-login-cc"
                            aria-label="Country code">
                      {COUNTRY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-neutral-400 pointer-events-none" />
                  </div>
                  <div className="relative flex-1">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                    <Input type="tel" inputMode="numeric" maxLength={10}
                           value={local}
                           onChange={(e) => setLocal(e.target.value.replace(/\D/g, "").slice(0, 10))}
                           className="h-14 pl-12 rounded-2xl text-base font-mono tracking-wider"
                           placeholder="98765 43210" data-testid="phone-input" />
                  </div>
                </div>

                <label className="text-xs uppercase tracking-wider font-bold text-neutral-500 mt-5 block">{t("choose_channel")}</label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <button onClick={() => setChannel("sms")}
                          className={`h-14 rounded-2xl font-bold text-sm border-2 inline-flex items-center justify-center gap-2 ${channel === "sms" ? "border-brand-sky bg-brand-sky/10 text-brand-sky" : "border-neutral-200 bg-white text-neutral-700"}`}
                          data-testid="channel-sms">
                    <Send className="w-4 h-4" /> {t("sms")}
                  </button>
                  <button onClick={() => setChannel("whatsapp")}
                          className={`h-14 rounded-2xl font-bold text-sm border-2 inline-flex items-center justify-center gap-2 ${channel === "whatsapp" ? "border-brand-leaf bg-brand-leaf/10 text-brand-leaf" : "border-neutral-200 bg-white text-neutral-700"}`}
                          data-testid="channel-whatsapp">
                    <MessageCircle className="w-4 h-4" /> {t("whatsapp")}
                  </button>
                </div>

                <Button onClick={() => sendOtp()} disabled={loading}
                        className="w-full mt-6 h-14 rounded-2xl font-bold text-base text-white"
                        style={{ background: primary }}
                        data-testid="send-otp-btn">
                  {loading ? "Sending..." : `${t("send_otp")} (${channel.toUpperCase()})`}
                </Button>
              </>
            ) : (
              <>
                <h2 className="font-heading text-2xl font-bold mb-1">{t("enter_otp")}</h2>
                <p className="text-neutral-600 text-sm mb-6">Sent via <b className="capitalize">{channel}</b> to <b>{cc} {local}</b></p>

                <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">6-digit OTP</label>
                <div className="relative mt-2">
                  <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                  <Input type="text" inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value)}
                         className="h-14 pl-12 rounded-2xl text-base font-mono tracking-[0.4em]"
                         placeholder="123456" data-testid="otp-input" />
                </div>

                {/* Only ask for the name on FIRST login. Returning users get
                    pre-filled (or no field at all) so they don't have to retype. */}
                {!userExists && (
                  <Input className="h-14 rounded-2xl text-base mt-4"
                         placeholder="Your name (one-time, for new accounts)"
                         value={name} onChange={(e) => setName(e.target.value)}
                         data-testid="name-input" />
                )}

                {/* Referral code (optional) — only for brand-new signups, and
                    only when the tenant has the setting on */}
                {!userExists && referralAtSignup && (
                  <Input className="h-14 rounded-2xl text-base mt-3 font-mono tracking-widest uppercase"
                         placeholder="Referral code (optional)"
                         value={refCode} onChange={(e) => setRefCode(e.target.value.toUpperCase())}
                         maxLength={12}
                         data-testid="referral-code-input" />
                )}

                <Button onClick={verifyOtp} disabled={loading || otp.length < 4}
                        className="w-full mt-6 h-14 rounded-2xl font-bold text-base text-white"
                        style={{ background: primary }}
                        data-testid="verify-otp-btn">
                  {loading ? "Verifying..." : t("verify_continue")}
                </Button>

                {/* Resend block: 30-sec countdown, then choose channel again. */}
                <div className="mt-5 pt-4 border-t border-neutral-100" data-testid="resend-block">
                  {resendSecs > 0 ? (
                    <div className="text-center text-sm text-neutral-500" data-testid="resend-countdown">
                      Resend available in <span className="font-bold text-neutral-700">{resendSecs}s</span>
                    </div>
                  ) : (
                    <>
                      <div className="text-center text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2">
                        Didn&rsquo;t get it? Resend via
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <button onClick={() => resendOtp("sms")} disabled={loading}
                                className="h-11 rounded-xl font-bold text-sm border-2 border-brand-sky text-brand-sky inline-flex items-center justify-center gap-2 hover:bg-brand-sky/10 disabled:opacity-50"
                                data-testid="resend-sms-btn">
                          <Send className="w-4 h-4" /> {t("sms")}
                        </button>
                        <button onClick={() => resendOtp("whatsapp")} disabled={loading}
                                className="h-11 rounded-xl font-bold text-sm border-2 border-brand-leaf text-brand-leaf inline-flex items-center justify-center gap-2 hover:bg-brand-leaf/10 disabled:opacity-50"
                                data-testid="resend-whatsapp-btn">
                          <MessageCircle className="w-4 h-4" /> {t("whatsapp")}
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <button onClick={() => { setStep("phone"); setResendSecs(0); }}
                        className="w-full mt-4 text-sm text-neutral-600 hover:text-brand-sky"
                        data-testid="change-phone-btn">
                  Change phone / channel
                </button>
              </>
            )}
          </div>

          <div className="mt-6 flex justify-center gap-4 text-sm text-neutral-600">
            <Link to="/delivery/login" className="hover:text-brand-sky" data-testid="delivery-login-link">{t("delivery_boy")} →</Link>
            <Link to="/admin/login" className="hover:text-brand-sky" data-testid="admin-login-from-customer">{t("admin")} →</Link>
          </div>
          {/* LegalFooter lives INSIDE the form column so it stacks BELOW the
              login card on mobile (the parent flex centers a single column —
              if LegalFooter were a sibling of this column it would sit next
              to the form on phones, eating half the screen width). */}
          <LegalFooter theme="light"/>
        </div>
      </div>
    </div>
  );
}
